/**
 * A COACH TYPING THEIR CLIENT'S BODY MEASUREMENTS IN — his word, 2026-09-19, in the Fable session:
 * *"yes a coach should be able to type a client's own body measurements in"*.
 *
 * Everfit's shape, read live at depth 3 (`docs/EVERFIT-MAP.md` §2c-ter): an outlined
 * `Update Results` beside the window selector opens `Update All Metrics` — a date picker at the
 * top right, then ONE ROW PER METRIC (label left, a number input with the unit as a suffix inside
 * the field), and a single ENABLED `Save`.
 *
 * ── WHAT THIS FILE IS, AND WHY IT IS NOT IN THE COMPONENT ────────────────────────────────────
 *
 * 🔴 **THE PAYLOAD IS THE SECURITY-RELEVANT HALF, SO IT IS A FUNCTION A TEST CAN DRIVE.** The live
 * policy `body_measurements_coach_insert` (`20260919224500`) admits a client's ACTIVE, ACCEPTED
 * coach for numbers only — its `with check` refuses any photo path. **Its sibling
 * `body_measurements_coach_update` does NOT**: both halves are the relationship test alone, so the
 * database would accept a coach setting, changing or NULLING a client's own photo paths, and
 * (because `with check` reads the NEW row) moving a row between two of that coach's clients.
 * MEASURED against the live database, rolled back, 2026-09-19 (`~/ht-logs/day-0919/coachwrite/`):
 * a coach INSERT carrying a photo path is refused 42501; the same coach's UPDATE setting one
 * touches **1 row**.
 *
 * ⚠️ **THAT IS HIS TO CLOSE — RLS IS HIS SURFACE — AND IT IS A LEDGER ROW, NOT A THING THIS
 * SESSION PATCHED.** What belongs here is the other half: a build that cannot do what the policy
 * fails to forbid. `measurementPayload` returns an object whose KEYS are `user_id`, `measured_at`
 * and filled measurement columns, and nothing else can be added to it by a caller — there is no
 * photo key to pass and no way to name a different `user_id` than the client the panel is reading.
 * The read struct and the write struct are separate, which is the codex's own rule: *a read struct
 * says what the row holds; a write struct says which columns I am authoritative about.*
 */

/** The measurement columns a coach may type, in the order the form draws them. */
export const MEASUREMENT_FIELDS = [
  "weight_kg", "body_fat_percent",
  "neck_cm", "shoulders_cm", "chest_cm", "waist_cm", "hips_cm",
  "left_upper_arm_cm", "right_upper_arm_cm",
  "left_forearm_cm", "right_forearm_cm",
  "left_thigh_cm", "right_thigh_cm",
  "left_calf_cm", "right_calf_cm",
] as const;
export type MeasurementField = (typeof MEASUREMENT_FIELDS)[number];

/** Stated, so a lossy edit cannot pass a comparison against its own output. */
export const MEASUREMENT_FIELD_COUNT = 15;

/**
 * ⚠️ THE PHOTO COLUMNS ARE NAMED HERE **SO THAT A TEST CAN ASSERT THEIR ABSENCE**, never so that
 * anything may write them. `body_measurements` has three, they are the client's own uploads, and
 * the one thing the coach's INSERT policy refuses outright is a row that carries one.
 */
export const PHOTO_COLUMNS = ["photo_front_path", "photo_side_path", "photo_back_path"] as const;

/** The label and the unit — the unit is drawn as a suffix INSIDE the field, as theirs is. */
export function fieldLabel(f: MeasurementField): string {
  switch (f) {
    case "weight_kg": return "Weight";
    case "body_fat_percent": return "Body fat";
    case "neck_cm": return "Neck";
    case "shoulders_cm": return "Shoulders";
    case "chest_cm": return "Chest";
    case "waist_cm": return "Waist";
    case "hips_cm": return "Hips";
    case "left_upper_arm_cm": return "Upper arm (L)";
    case "right_upper_arm_cm": return "Upper arm (R)";
    case "left_forearm_cm": return "Forearm (L)";
    case "right_forearm_cm": return "Forearm (R)";
    case "left_thigh_cm": return "Thigh (L)";
    case "right_thigh_cm": return "Thigh (R)";
    case "left_calf_cm": return "Calf (L)";
    case "right_calf_cm": return "Calf (R)";
  }
}

export function fieldUnit(f: MeasurementField): string {
  if (f === "weight_kg") return "kg";
  if (f === "body_fat_percent") return "%";
  return "cm";
}

/**
 * A plausible band per field, and it exists to catch a TYPO rather than to police a body.
 *
 * ⚠️ THE BANDS ARE WIDE ON PURPOSE. A measurement this refuses is a measurement a coach cannot
 * record, so the only values worth refusing are the ones no body produces — a slipped decimal
 * point (`764` for `76.4`) or a stray digit. ⚠️ AND THE DATABASE HAS ITS OWN, WHICH IS WHY THESE
 * ARE NOT THE GUARD: a live rehearsal's `weight_kg = 999` came back `23514`, a CHECK constraint
 * refusing it before any policy was consulted — so a value this lets through can still be refused
 * server-side, and the form reports what the server said rather than promising it will be taken.
 */
export function fieldBand(f: MeasurementField): { min: number; max: number } {
  if (f === "weight_kg") return { min: 20, max: 400 };
  if (f === "body_fat_percent") return { min: 1, max: 75 };
  return { min: 5, max: 250 };
}

export type MeasurementRow = { user_id: string; measured_at: string } & Partial<
  Record<MeasurementField, number>
>;

export type PayloadResult =
  | { ok: true; row: MeasurementRow; filled: number }
  | { ok: false; why: string };

/**
 * Build the one row a coach's save writes.
 *
 * ⚠️ AN EMPTY FIELD IS AN ABSENCE, NEVER A ZERO. A coach who measures a waist and nothing else
 * must not write `0` into fourteen columns — the panel would then chart a client whose chest
 * vanished. Empty (and whitespace) is omitted from the row entirely.
 *
 * ⚠️ AND A ROW WITH NOTHING IN IT IS REFUSED RATHER THAN WRITTEN. An empty measurement is a date
 * with no measurement attached; it would draw a gap in a chart and read as a reading of zero.
 *
 * 🔴 `measured_at` IS MIDDAY, NOT MIDNIGHT, AND THAT IS NOT COSMETIC. The picker gives a day key
 * and the column is a timestamp: `2026-09-19T00:00:00` local, converted to UTC anywhere east of
 * Greenwich, lands on the 18th — the same day-shift `dayKeyOf` exists to prevent one screen over.
 * Midday is the hour no timezone on earth can push across a date boundary.
 */
export function measurementPayload(
  clientId: string,
  dayKey: string,
  values: Partial<Record<MeasurementField, string>>,
): PayloadResult {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return { ok: false, why: "Pick a date." };
  if (clientId.trim() === "") return { ok: false, why: "No client." };

  const row: MeasurementRow = { user_id: clientId, measured_at: `${dayKey}T12:00:00` };
  let filled = 0;

  for (const f of MEASUREMENT_FIELDS) {
    const raw = (values[f] ?? "").trim();
    if (raw === "") continue;
    const n = Number(raw.replace(",", "."));
    if (!Number.isFinite(n)) return { ok: false, why: `${fieldLabel(f)} is not a number.` };
    const band = fieldBand(f);
    if (n < band.min || n > band.max) {
      return { ok: false, why: `${fieldLabel(f)} looks wrong — ${String(band.min)}–${String(band.max)} ${fieldUnit(f)}.` };
    }
    row[f] = n;
    filled += 1;
  }

  if (filled === 0) return { ok: false, why: "Type at least one measurement." };
  return { ok: true, row, filled };
}

/** What the coach is told after a successful save — the date and how much landed. */
export function savedSentence(filled: number, dayKey: string): string {
  const n = filled === 1 ? "1 measurement" : `${String(filled)} measurements`;
  return `Saved ${n} for ${dayKey}.`;
}
